import { IMultiUrlImage } from '../pipes/multi-url-image.pipe';
import { IDeadlineSchedule, IDeviatingDeadline } from './i-bakery';
import { IBakeryCountry } from './i-bakery-country';
import { SameDayDeliverySettings } from './same-day-delivery-settings';
import { IClosedDay } from './i-closed-day';

export interface IProductType {
  id?: number;
  name?: string;
  description?: string;
  badgeColor?: string;
  badgeText?: string;
  category?: {
    id: number;
    name: string;
  };
  picture?: number;
  tags?: any[];
  bakery?: IProductTypeBakery;
  sizes?: IProductTypeSize[];
  allergens?: any[];
  ingredients?: string;
  hasCustomDeadlines?: boolean;
  deadlines?: any;
  daysUnavailable?: string[];
  greetingCard?: IProductTypeGreetingCard;
  deliveryAvailable?: boolean;
  pickupAvailable?: boolean;
  mailDeliveryAvailable?: boolean;
  blockedOutlets?: any[];
  categoryPictureId?: number;
  structureType?: string;
  minQuantity: number;
  maxQuantity: number;
  minQuantityPerProduct: number;
  stockSettings?: IProductTypeStockSettings;
  showGroupName: boolean;
  increment?: number;
  pictureIsVertical?: boolean;
  categoryPictureIsVertical?: any;
  customNote?: string;
  colorTypeOfCustomNote?: string;
  customNoteColor?: string;
  useCustomNoteColor?: boolean;
  customNoteTextColor?: string;
  marketplaceCustomNote?: string;
  marketplaceColorTypeOfCustomNote?: string;
  marketplaceCustomNoteColor?: string;
  marketplaceUseCustomNoteColor?: boolean;
  marketplaceCustomNoteTextColor?: string;
  images?: IMultiUrlImage[];
  categoryPicture?: IMultiUrlImage;
  gallery?: number[];
  image?: IMultiUrlImage;
  discount?: IProductTypeDiscount;
  availability?: IProductTypeAvailability;
  isFreeOfAllergen?: boolean;
  hasPickupInExternalOutlet?: boolean;
  externalOutletsCount?: number;
  outletsCount?: number;
  details?: IQuestionHelpTextDetails;
  parentType?: {
    name: string;
    description: string;
    images: IMultiUrlImage[];
    sizes: IProductTypeSize[]
  };
  upsellProductsTitle?: string;

  sameDayDeliverySettings: SameDayDeliverySettings;

  requestUrl?: string;
  requestForm?: number;
  useCustomRequestFormLink?: boolean;
  customRequestFormLink?: string;

  // Syntetics property
  /**Deprecated*/
  pieces?: boolean;
}

export interface IProductTypeStockSettings {
  isEnabled: boolean;
  mode: PRODUCT_TYPE_STOCK_MODE;
  remainingInStock?: number;
  dailyLimit?: number;
  soldOutDates?: string[];
}

export enum PRODUCT_TYPE_STOCK_MODE {
  COUNT_DOWN = 'COUNT_DOWN',
  DAILY_LIMIT = 'DAILY_LIMIT',
}

export interface IProductTypeBakery {
  id: number;
  name: string;
  description: string;
  email: string;
  country: IBakeryCountry;
  area: {
    id: number;
    name: string;
    country: string;
    postNumbers: {
      id: number;
      rangeBegin: number;
      rangeEnd: number;
    }[];
    timezone: string;
    deliveryVat: number;
    pickupVat: number;
    productVat: number;
    daysToWaitAfterDelivery: number;
    accountingEmailAddress: string;
    invoiceVat: number;
    invoiceFee: number;
    invoiceFeeEnabled: boolean;
    paymentSlipSenderEmail: string;
    paymentSlipSenderTitle: string;
  };
  cities: string[];
  isDeliveryAvailable: boolean;
  isMailDeliveryAvailable: boolean;
  schedule: IDeadlineSchedule;
  customSchedule: string;
  isPickupAvailable: boolean;
  payInStoreMarketplaceOnlyMethod: boolean;
  payInStoreWebShopOnlyMethod: boolean;
  greetingCardApproved: boolean;
  greetingCardMaxLength: number;
  greetingCardPrice: number;
  greetingCardVatRate: number;
  position: string;
  image?: IMultiUrlImage;
  picture: number;
  pickupLocations: string[];
  deliveryToLocations: string[];
  webSiteUrl: string;
  deviatingOrderDeadlines: IDeviatingDeadline[];
  closedDays?: IClosedDay[];
  holidays?: IClosedDay[];

  thirdPartyDeliveryAllowedOnMarketplace: boolean;
  thirdPartyDeliveryAllowedOnWebshop: boolean;
  thirdPartyDeliveryEnabled: boolean;
}

export interface IProductTypeSize {
  details: IProductTypeSizeDetails;
  productNumber: string;
  price: number;
  commentAllowed: boolean;
  printingPictures: any[];
  printingTexts: any[];
  products: IProductTypeSizeProduct[];
  extraGroups: any[];
  questions: any[];
  deposits: any[];
  hasCustomPrice?: boolean;

  // Syntetics property
  minPrice?: number;
}

export interface IProductTypeSizeDetails {
  id: number;
  value: number;
  definition: string;
  pieces: boolean;
  minimumQuantity: number;
  increment: number;
  measurement: string;
  shape: string;
  unit: {
    id: number;
    name: string;
    key?: string;
  };
}

export interface IProductTypeVariant {
  description: string;
  id: number;
  isStandard: boolean;
  mostPopular: boolean;
  name: string;
  priority: number;

  // Syntetics property
  price?: number;
  image?: IMultiUrlImage;
}

export interface IProductTypeSizeProduct {
  id: number;
  name: string;
  productNumber: any;
  price: number;
  commentAllowed: any;
  hasCustomPrice?: boolean;
  variant: IProductTypeVariant;
  image?: IMultiUrlImage;
}

export interface IProductTypeGreetingCard {
  available?: boolean;
  maxLength?: number;
  price?: number;
  vatRate?: number;
}

export interface IProductTypeDiscount {
  id?: number;
  value?: number;
  expirationDate?: Date;
  infinite?: boolean;
}

export interface IQuestionHelpTextDetails {
  sizeSectionTitle: string;
  sizeSectionHelpText?: string;
  filingSectionTitle: string;
  filingSectionHelpText?: string;
}

export interface IProductTypeAvailability {
  availableAlways: boolean;
  recurrent?: boolean;
  availablePeriod?: IAvailabilityPeriod;
  unavailablePeriod?: IAvailabilityPeriod[];
}

export interface IAvailabilityPeriod {
  begin: string;
  end: string;
  recurrent?: boolean;
}
